import { ACCESS_POLICIES, levelOf } from "../../../lib/access";
import { PERMISSION_GROUPS } from "../../../lib/organization";
import { useRoleForm } from "../../SuperAdmin/useRoleForm";
import { Icon } from "../../../components/msd/Icon";
import { Btn, Field, INPUT, Seg2, Sheet, Switch, cx } from "../../../components/msd/ui";
import { FieldGroup, Gico, RemoveBtn, TG_LIST, TG_ROW, plural } from "./kit";
import { PERMISSION_ICON, POLICY_ICON } from "./roleIcons";

// levelOf() answers false for none; the control wants a string.
const LEVELS = [
  ["none", "None"],
  ["read", "Read"],
  ["edit", "Edit"],
];

const OFF = "rgba(120,120,128,.16)";

/** Creating or editing one role — the same form and write as Super Admin's
 *  role drawer (useRoleForm). */
export default function RoleSheet({ role, onClose }) {
  const { form, set, errors, isNew, codeLocked, setLabel, setCode, toggle, setLevel, onSave, onDelete, pending } =
    useRoleForm(role, null, onClose);

  return (
    <Sheet
      open
      title={isNew ? "New role" : "Edit role"}
      onClose={onClose}
      testid="role-sheet"
      footer={
        <>
          {!isNew && !form.is_system && (
            <RemoveBtn onClick={onDelete} disabled={pending}>
              Delete role
            </RemoveBtn>
          )}
          <Btn onClick={onClose}>Cancel</Btn>
          <Btn variant="pri" onClick={onSave} disabled={pending}>
            {isNew ? "Create role" : "Save"}
          </Btn>
        </>
      }
    >
      {!isNew && (
        <div className="-mt-2 text-[13px] text-msd-muted">{plural(role.user_count || 0, "person", "people")}</div>
      )}

      <Field label="Role name" error={errors.label}>
        <input
          type="text"
          className={INPUT}
          value={form.label}
          placeholder="For example, Regional manager"
          onChange={(e) => setLabel(e.target.value)}
        />
      </Field>

      <div className="grid grid-cols-2 gap-3 max-[640px]:grid-cols-1">
        <Field label="Short name" error={errors.short_label}>
          <input
            type="text"
            className={INPUT}
            value={form.short_label}
            onChange={(e) => set({ short_label: e.target.value })}
          />
        </Field>
        <Field label="Role code" error={errors.code}>
          <span className="relative block">
            <input
              type="text"
              className={cx(INPUT, codeLocked && "cursor-not-allowed pr-10")}
              value={form.code}
              disabled={codeLocked}
              onChange={(e) => setCode(e.target.value)}
            />
            {codeLocked && (
              <span className="pointer-events-none absolute right-3 top-[15px] flex text-msd-muted">
                <Icon name="lock" size={15} stroke={2} />
              </span>
            )}
          </span>
        </Field>
      </div>

      <FieldGroup label="Who can they see">
        <div className={TG_LIST} role="radiogroup" aria-label="Access policy">
          {ACCESS_POLICIES.map((p) => {
            const on = form.access_policy === p.id;
            return (
              <button
                key={p.id}
                type="button"
                role="radio"
                aria-checked={on}
                onClick={() => set({ access_policy: p.id })}
                className={cx(
                  "flex cursor-pointer items-center gap-3 border-0 px-[14px] py-3 text-left text-inherit",
                  on ? "bg-[#F2F7FF] [:root[data-theme=dark]_&]:bg-[#14244A]" : "bg-msd-surface",
                )}
              >
                <Gico icon={POLICY_ICON[p.id] || "map"} bg={on ? "var(--btn)" : OFF} fg={on ? "#fff" : "var(--muted)"} />
                <b className="grow text-[15px] font-semibold">{p.label}</b>
                {on && <Icon name="check" size={18} stroke={2.6} color="var(--ink)" />}
              </button>
            );
          })}
        </div>
      </FieldGroup>

      {PERMISSION_GROUPS.map(({ label, items }, i) => (
        <FieldGroup
          key={label || "_"}
          label={label || "Features"}
          error={i === PERMISSION_GROUPS.length - 1 ? errors.permissions : undefined}
        >
          <div className={TG_LIST}>
            {items.map((p) => {
              const level = p.levelled ? levelOf(form.permissions, p.id) : form.permissions.includes(p.id);
              return (
                <div key={p.id} className={cx(TG_ROW, "flex-wrap")}>
                  <Gico
                    icon={PERMISSION_ICON[p.id] || "shield"}
                    bg={level ? "var(--btn)" : OFF}
                    fg={level ? "#fff" : "var(--muted)"}
                  />
                  <b className="min-w-0 grow text-[15px] font-semibold">{p.label}</b>
                  {p.levelled ? (
                    // Edit implies read, so this is one choice of three, not two switches.
                    <Seg2
                      small
                      options={LEVELS}
                      value={level || "none"}
                      onChange={(v) => setLevel(p.id, v === "none" ? false : v)}
                      label={p.label}
                    />
                  ) : (
                    <Switch on={level} onChange={() => toggle(p.id)} label={p.label} />
                  )}
                </div>
              );
            })}
          </div>
        </FieldGroup>
      ))}
    </Sheet>
  );
}
